import React, { useState } from 'react';
import { Col, Row } from 'components/Core/Grid';
import ToggleSwitch from 'components/Core/ToggleSwitch';
import GatedSubscribeForm from '../GatedSubscribeForm';
import { HvacFormContainer, FormContainer, Disclaimer } from './styles';
import EmbedForm from '../EmbeddableForm';
import { calcEnergySavings, calcEnergySavingsHeating } from './calcs';
import SeerEnergyForm from './coolingForm';
import SeerEnergyHeatingForm from './heatingForm';
import SeerEnergyChart from './chart';

const SeerEnergyCalculator = ({ gatedForm, handleToggleClick, pathUrl, ...otherProps }) => {
  const [coolingValues, setCoolingValues] = useState({});
  const [heatingValues, setHeatingValues] = useState({});
  const [gateFormCookieIncrease, setGateFormCookieIncrease] = useState(0);
  const [indexSelected, toggleIndexSelected] = useState(0);

  const toggleClick = () => {
    toggleIndexSelected(indexSelected === 0 ? 1 : 0);
    handleToggleClick?.(indexSelected === 0 ? 'heat' : 'cool');
  };

  const calcHeating = (values) => {
    const { location, energyRate, avgBTUSeason, currentEfficiency, newEfficiency } = values;

    dataLayer.push({
      event: 'calculator',
      calculator_type: 'HVAC SEER',
      climate_region: location,
      avg_electricity_rate: energyRate,
      avgBTUSeason,
      current_seer: currentEfficiency,
      new_seer: newEfficiency,
    });

    setGateFormCookieIncrease(gateFormCookieIncrease + 1);

    const calculatedResults = calcEnergySavingsHeating(
      energyRate,
      avgBTUSeason,
      currentEfficiency,
      newEfficiency,
    );

    setHeatingValues({ ...values, ...calculatedResults });
  };

  const calcCooling = (values) => {
    const { location, energyRate, capacity, currentEfficiency, newEfficiency } = values;

    dataLayer.push({
      event: 'calculator',
      calculator_type: 'HVAC SEER',
      climate_region: location,
      avg_electricity_rate: energyRate,
      ac_capacity: capacity,
      current_seer: currentEfficiency,
      new_seer: newEfficiency,
    });

    setGateFormCookieIncrease(gateFormCookieIncrease + 1);

    const calculatedResults = calcEnergySavings(
      location,
      energyRate,
      capacity,
      currentEfficiency,
      newEfficiency,
    );

    setCoolingValues({ ...values, ...calculatedResults });
  };

  return (
    <FormContainer
      className={indexSelected === 1 ? 'heat' : ''}
      isEmbed={otherProps.isEmbed}
      {...otherProps}
    >
      <div className="section-tool-page-form--wrapper">
        <div className="section-tool-page-form--container">
          <HvacFormContainer>
            <Row>
              <Col lg={10} md={10} sm={24} xs={24}>
                <GatedSubscribeForm gatedForm={gatedForm} increaseCookie={gateFormCookieIncrease}>
                  <ToggleSwitch
                    titles={['Cooling', 'Heating']}
                    handleClick={toggleClick}
                    indexSelected={indexSelected}
                    checkedState={indexSelected === 1}
                  />
                  {indexSelected === 0 && (
                    <SeerEnergyForm
                      saveValues={(vals) => setCoolingValues(vals)}
                      onCalc={calcCooling}
                      pathUrl={pathUrl}
                      {...coolingValues}
                    />
                  )}
                  {indexSelected === 1 && (
                    <SeerEnergyHeatingForm
                      saveValues={(vals) => setHeatingValues(vals)}
                      onCalc={calcHeating}
                      pathUrl={pathUrl}
                      {...heatingValues}
                    />
                  )}
                </GatedSubscribeForm>
              </Col>
              <Col lg={14} md={14} sm={24} xs={24} className="sticky-container">
                {indexSelected === 0 && (
                  <SeerEnergyChart
                    title="Cooling Savings"
                    subtitle={`${coolingValues?.currentEfficiency || 0} SEER vs ${
                      coolingValues?.newEfficiency || 0
                    } SEER on a ${coolingValues?.capacity || 0} BTU Air Conditioner`}
                    {...coolingValues}
                  />
                )}
                {indexSelected === 1 && (
                  <>
                    <SeerEnergyChart
                      title="Heating Savings"
                      subtitle={`${heatingValues?.currentEfficiency || 0} AFUE vs ${
                        heatingValues?.newEfficiency || 0
                      } AFUE`}
                      {...heatingValues}
                    />
                    <Disclaimer type="p" textSize="p-sm">
                      This calculator is only intended to give you a rough estimate. The savings are
                      not guaranteed, and is based on using natural gas as fuel and the household
                      requiring 100 million BTU’s per avg heating season.
                    </Disclaimer>
                  </>
                )}
              </Col>
            </Row>
          </HvacFormContainer>
        </div>
      </div>
    </FormContainer>
  );
};

export default EmbedForm(SeerEnergyCalculator);
